import type { PropsWithChildren } from 'react';
import { useCallback, useMemo, useState } from 'react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import type { IExportTranscriptionContextValue } from './ExportTranscriptionContext';
import ExportTranscriptionContext from './ExportTranscriptionContext';
import type { Transcription } from '../../entities';
import { ExportType } from '../../entities';
import {
  handleDownloadGuitarPro,
  handleDownloadLilypond,
  handleDownloadMidi,
  handleDownloadMp3,
  handleDownloadMusicJson,
  handleDownloadMusicXML,
  handleDownloadPdf,
  handleDownloadPianoRoll,
  handleDownloadUnquantizedMidi,
} from '../../utils/download';

const ExportTranscriptionProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { t } = useTranslation();
  const [isDownloadingExportFile, setIsDownloadingExportFile] = useState<boolean>(false);
  const [exportErrorMessage, setExportErrorMsg] = useState<null | string>(null);

  const onDownload = useCallback(
    (transcription: Transcription, o: ExportType) => {
      setIsDownloadingExportFile(true);
      setExportErrorMsg(null);
      let resPromise = null;
      switch (o) {
        case ExportType.MIDI:
          resPromise = handleDownloadMidi(transcription);
          break;
        case ExportType.MUSIC_XML:
          resPromise = handleDownloadMusicXML(transcription);
          break;
        case ExportType.PDF:
          resPromise = handleDownloadPdf(transcription);
          break;
        case ExportType.PIANO_ROLL:
          resPromise = handleDownloadPianoRoll(transcription);
          break;
        case ExportType.GUITAR_PRO:
          resPromise = handleDownloadGuitarPro(transcription);
          break;
        case ExportType.UNQUANTIZED_MIDI:
          resPromise = handleDownloadUnquantizedMidi(transcription);
          break;
        case ExportType.MP3:
          resPromise = handleDownloadMp3(transcription);
          break;
        case ExportType.MUSIC_JSON:
          resPromise = handleDownloadMusicJson(transcription);
          break;
        case ExportType.LILYPOND:
          resPromise = handleDownloadLilypond(transcription);
          break;
        default:
          console.warn(`unknown download option ${o}`);
          setIsDownloadingExportFile(false);
          break;
      }

      if (resPromise !== null) {
        resPromise
          .catch((ex: any) => {
            setExportErrorMsg(t('home.songbook.error.download_failed_msg'));
            console.error(ex);
          })
          .finally(() => setIsDownloadingExportFile(false));
      }
    },
    [t],
  );

  const clearError = useCallback(() => setExportErrorMsg(null), []);

  const ctxtVal = useMemo<IExportTranscriptionContextValue>(
    () => ({
      isDownloadingExportFile,
      exportErrorMessage,
      onDownload,
      clearError,
    }),
    [isDownloadingExportFile, exportErrorMessage, onDownload, clearError],
  );

  return (
    <ExportTranscriptionContext.Provider value={ctxtVal}>
      {children}
    </ExportTranscriptionContext.Provider>
  );
};

export default ExportTranscriptionProvider;
